import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Check, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  actionLabel, contentForTeam, jonathanStatement, prioritizationMode, prioritySource, qualifyingConcerns, rankConcerns,
  recommendConcern, selectHeadline, selectRecommendation, supportedOutcomes, whyAmISeeingThis,
} from "@/lib/nextMoveLogic";
import { JONATHAN_PORTRAIT } from "@/components/landing/constants";
import { BackRow, Disclaimer } from "@/components/nextmove/shared";
import { BookingScreen } from "@/components/nextmove/BookingForm";
import { AssessmentContactScreen } from "@/components/nextmove/AssessmentScreens";
import { useNavigate } from "react-router-dom";
import {
  NarrowScreen, OutcomeScreen, PriorityScreen, SuggestionScreen, TeamSizeScreen,
} from "@/components/nextmove/PrioritizationScreens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADVANCE_MS = 180;

/**
 * FREE BUSINESS DIAG modal (v0.6).
 * team → q1..q10 → [priority | outcome → narrow] (→ suggest) → q11 → results → booking/email
 */
export default function NextMoveModal({ open, onOpenChange }) {
  const [content, setContent] = useState(null);
  const [stage, setStage] = useState("team");
  const [teamSize, setTeamSize] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [priority, setPriority] = useState(null); // "q3" | "other" | "skipped" | null
  const [otherText, setOtherText] = useState("");
  const [accepted, setAccepted] = useState(false); // priority came from "Start Here"
  const [outcome, setOutcome] = useState(null);    // {id, questions} chosen on outcome screen
  const [suggest, setSuggest] = useState(null);    // {eligible, returnTo}
  const [q11Back, setQ11Back] = useState("question");
  const [q11, setQ11] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const navigate = useNavigate();

  const resetPriority = () => {
    setPriority(null); setOtherText(""); setAccepted(false); setOutcome(null); setSuggest(null);
  };

  useEffect(() => {
    if (!open) return;
    setStage("team"); setTeamSize(null); setQIdx(0); setAnswers({});
    resetPriority(); setQ11(null); setSubmissionId(null);
  }, [open]);

  useEffect(() => {
    if (!open || content) return;
    fetch(`${API}/next-move/content`).then((r) => r.json()).then(setContent).catch(() => {
      toast.error("Couldn't load the diagnostic — please try again in a moment.");
    });
  }, [open, content]);

  const answersAsList = useMemo(
    () => Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer })),
    [answers]
  );
  const qualifying = useMemo(() => qualifyingConcerns(answersAsList), [answersAsList]);
  const ranked = useMemo(
    () => rankConcerns(answersAsList, priority, otherText, teamSize),
    [answersAsList, priority, otherText, teamSize]
  );
  const source = prioritySource(answersAsList, priority, otherText, accepted);
  const totalQuestions = content?.questions?.length || 10;

  const chooseTeam = (id) => {
    if (id !== teamSize) resetPriority();
    setTeamSize(id);
    setTimeout(() => { setQIdx(0); setStage("question"); }, ADVANCE_MS);
  };

  const chooseAnswer = (qid, answer) => {
    if (answers[qid] !== answer) resetPriority();
    const next = { ...answers, [qid]: answer };
    setAnswers(next);
    setTimeout(() => {
      if (qIdx < totalQuestions - 1) return setQIdx((n) => n + 1);
      const mode = prioritizationMode(Object.entries(next).map(([questionId, a]) => ({ questionId, answer: a })));
      if (mode === "simple") return setStage("priority");
      if (mode === "outcomes") return setStage("outcome");
      goToQ11("question");
    }, ADVANCE_MS);
  };

  const goToQ11 = (from) => { setQ11Back(from); setStage("q11"); };
  const pickExplicit = (id, from) => { setPriority(id); setAccepted(false); if (from) goToQ11(from); };
  const skipPriority = (from) => { setPriority("skipped"); setAccepted(false); goToQ11(from); };
  const openSuggest = (eligible, returnTo) => { setSuggest({ eligible, returnTo }); setStage("suggest"); };
  const backToQ10 = () => { setQIdx(totalQuestions - 1); setStage("question"); };

  const pickOutcome = (o) => {
    setOutcome(o);
    if (o.questions.length === 1) return pickExplicit(o.questions[0], "outcome");
    setStage("narrow");
  };

  const persistAndShowResults = async () => {
    try {
      const payload = {
        answers: answersAsList.map((a) => ({ question_id: a.questionId, answer: a.answer })),
        priority: priority || "skipped",
        other_text: priority === "other" ? otherText.trim() : null,
        q11: q11 || "skipped",
        team_size: teamSize,
        priority_source: source,
        ranked_concerns: ranked.map((rc) => ({
          question_id: rc.questionId, severity: rc.severity, other_text: rc.otherText,
        })),
      };
      const r = await fetch(`${API}/next-move/submissions`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) setSubmissionId((await r.json()).id);
      else console.warn("Diag submission not saved", r.status, await r.text());
    } catch (e) {
      console.warn("Diag submission not saved", e?.message); // never blocks the owner's results
    }
    setStage("results");
  };

  if (!content) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="next-move-modal" aria-describedby={undefined} className="max-w-md bg-cvc-cream text-cvc-ink">
          <DialogTitle className="sr-only">Loading diagnostic</DialogTitle>
          <div className="flex items-center justify-center py-12 gap-3 font-body text-cvc-ink-soft">
            <Spinner size={20} className="animate-spin" /> Loading…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const recommendedId = suggest ? recommendConcern(answersAsList, teamSize, suggest.eligible) : null;
  const view = contentForTeam(content, teamSize);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="next-move-modal"
        aria-describedby={undefined}
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cvc-cream text-cvc-ink p-0"
      >
        <DialogTitle className="sr-only">FREE BUSINESS DIAG — What's Your Next Move?</DialogTitle>
        {stage === "team" && (
          <TeamSizeScreen content={view} teamSize={teamSize} onChoose={chooseTeam}
            onBack={() => onOpenChange(false)} />
        )}
        {stage === "question" && (
          <QuestionScreen
            content={view} index={qIdx} totalQuestions={totalQuestions}
            answers={answers} onChoose={chooseAnswer}
            onBack={() => { if (qIdx > 0) setQIdx((n) => n - 1); else setStage("team"); }}
          />
        )}
        {stage === "priority" && (
          <PriorityScreen
            content={view} qualifying={qualifying} priority={priority} otherText={otherText}
            onSelect={(id) => pickExplicit(id)} setOtherText={setOtherText}
            onHelp={() => openSuggest(null, "priority")}
            onSkip={() => skipPriority("priority")}
            onNext={() => goToQ11("priority")}
            onBack={backToQ10}
          />
        )}
        {stage === "outcome" && (
          <OutcomeScreen
            content={view} outcomes={supportedOutcomes(answersAsList)}
            helpFirst={answers.q10 === "very_true"} priority={priority} otherText={otherText}
            onPickOutcome={pickOutcome} onOther={() => pickExplicit("other")} setOtherText={setOtherText}
            onHelp={() => openSuggest(null, "outcome")}
            onSkip={() => skipPriority("outcome")}
            onNext={() => goToQ11("outcome")}
            onBack={backToQ10}
          />
        )}
        {stage === "narrow" && outcome && (
          <NarrowScreen
            content={view} outcome={outcome}
            onSelect={(id) => pickExplicit(id, "narrow")}
            onHelp={() => openSuggest(outcome.questions, "narrow")}
            onBack={() => setStage("outcome")}
          />
        )}
        {stage === "suggest" && recommendedId && (
          <SuggestionScreen
            content={view} recommendedId={recommendedId}
            onAccept={() => { setPriority(recommendedId); setAccepted(true); goToQ11(suggest.returnTo); }}
            onReject={() => setStage(suggest.returnTo)}
          />
        )}
        {stage === "q11" && (
          <Q11Screen
            content={view} q11={q11} setQ11={setQ11}
            onNext={() => { setQ11((v) => v || "skipped"); persistAndShowResults(); }}
            onBack={() => (q11Back === "question" ? backToQ10() : setStage(q11Back))}
          />
        )}
        {stage === "results" && (
          <ResultsScreen
            content={view} ranked={ranked} source={source} q11={q11}
            onBook={() => setStage("booking")} onEmail={() => setStage("email")}
            onAssess={() => setStage("assessment")}
          />
        )}
        {stage === "assessment" && (
          <AssessmentContactScreen content={view} submissionId={submissionId} teamSize={teamSize}
            onReady={(token) => { onOpenChange(false); navigate(`/assessment?t=${token}`); }}
            onBack={() => setStage("results")} />
        )}
        {stage === "booking" && (
          <BookingScreen submissionId={submissionId}
            onDone={() => onOpenChange(false)} onBack={() => setStage("results")} />
        )}
        {stage === "email" && (
          <EmailScreen content={view} ranked={ranked} submissionId={submissionId} teamSize={teamSize}
            onDone={() => setStage("results")} onBack={() => setStage("results")} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---- Screens ---------------------------------------------------------------
function QuestionScreen({ content, index, totalQuestions, answers, onChoose, onBack }) {
  const q = content.questions[index];
  const current = answers[q.id];
  return (
    <div className="p-7 md:p-10">
      <BackRow onBack={onBack} progress={`Question ${index + 1} of ${totalQuestions}`} />
      {index === 0 && (
        <p data-testid="nm-opening-instruction" className="font-body text-sm text-cvc-ink-soft mb-4 leading-relaxed">
          {content.opening_instruction}
        </p>
      )}
      <p className="text-[11px] tracking-[0.22em] uppercase font-body font-semibold text-cvc-ink-soft">{q.title}</p>
      <h2 data-testid={`nm-q-${q.id}-statement`} className="font-heading text-2xl md:text-3xl text-cvc-navy mt-3 leading-snug">
        {q.statement}
      </h2>
      {content.question_hints?.[q.id] && (
        <p data-testid={`nm-q-${q.id}-solo-hint`} className="font-body text-sm italic text-cvc-ink-soft mt-2">
          {content.question_hints[q.id]}
        </p>
      )}
      <div className="mt-6 flex flex-col gap-2.5">
        {content.answer_choices.map((c) => {
          const selected = current === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`nm-q-${q.id}-answer-${c.id}`}
              onClick={() => onChoose(q.id, c.id)}
              aria-pressed={selected}
              className={`text-left px-5 py-4 rounded-xl border font-body text-base transition-colors
                ${selected
                  ? "bg-cvc-navy text-cvc-cream border-cvc-navy"
                  : "bg-white text-cvc-ink border-cvc-border hover:border-cvc-navy hover:bg-cvc-parchment"}`}
            >
              <span className="flex items-center gap-3">
                {selected && <Check size={18} weight="bold" />}
                {c.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function Q11Screen({ content, q11, setQ11, onNext, onBack }) {
  return (
    <div className="p-7 md:p-10">
      <BackRow onBack={onBack} progress="One more thing" />
      <p className="text-[11px] tracking-[0.22em] uppercase font-body font-semibold text-cvc-ink-soft">{content.q11.intro}</p>
      <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy mt-3 leading-snug">{content.q11.statement}</h2>
      <div className="mt-6 flex flex-col gap-2.5">
        {content.q11.choices.map((c) => {
          const selected = q11 === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`nm-q11-${c.id}`}
              onClick={() => { setQ11(c.id); setTimeout(onNext, 180); }}
              className={`text-left px-5 py-4 rounded-xl border font-body text-base transition-colors
                ${selected ? "bg-cvc-navy text-cvc-cream border-cvc-navy" : "bg-white border-cvc-border hover:bg-cvc-parchment"}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <div className="mt-5 text-right">
        <button
          type="button"
          data-testid="nm-q11-skip"
          onClick={() => { setQ11("skipped"); onNext(); }}
          className="font-body text-sm text-cvc-ink-soft underline underline-offset-4 hover:text-cvc-ink"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

function ResultsScreen({ content, ranked, source, q11, onBook, onEmail, onAssess }) {
  const [whyOpen, setWhyOpen] = useState(false);
  const headline = selectHeadline(content.headlines, ranked);
  const consultCopy = content.consultation_invitations[q11] || content.consultation_invitations.default;
  const whyFragments = whyAmISeeingThis(content.why_explanations, source);
  const noConcerns = ranked.length === 0;

  return (
    <div className="p-7 md:p-10">
      <p data-testid="nm-results-eyebrow" className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">Your results</p>
      <h1 data-testid="nm-results-headline" className="font-heading text-3xl md:text-4xl text-cvc-navy mt-3 leading-tight">{headline.headline}</h1>
      <p className="font-body text-cvc-ink-soft mt-3 leading-relaxed">{headline.supporting}</p>

      {!noConcerns && (
        <div className="mt-6 flex flex-col gap-5">
          {ranked.map((rc, idx) => {
            const rec = selectRecommendation(content.recommendations, rc);
            const voice = jonathanStatement(content, rc, idx);
            const title =
              rc.questionId === "other"
                ? (rc.otherText || "Something else")
                : content.questions.find((q) => q.id === rc.questionId)?.title;
            return (
              <div key={`${rc.questionId}-${idx}`} data-testid={`nm-result-${idx}`} className="rounded-xl bg-white border border-cvc-border p-5">
                <p className="text-[10px] tracking-[0.2em] uppercase font-body font-semibold text-cvc-red">
                  {idx === 0 ? "Your Next Move" : "Also worth noticing"}: {title}
                </p>
                <p className="mt-3 font-heading text-base text-cvc-navy font-semibold">{actionLabel(idx)}</p>
                <p className="font-body text-cvc-ink mt-1 leading-relaxed text-[15px]">{rec?.action}</p>
                <p data-testid={`nm-result-${idx}-jonathan-label`} className="mt-4 font-heading text-sm text-cvc-navy font-semibold">{voice.label}</p>
                <p data-testid={`nm-result-${idx}-jonathan-text`} className="font-body text-cvc-ink mt-1 leading-relaxed italic text-[15px]">&ldquo;{voice.text}&rdquo;</p>
                {idx === 0 && (
                  <div className="mt-4 pt-4 border-t border-cvc-border flex items-center gap-3">
                    <img src={JONATHAN_PORTRAIT} alt="Jonathan Johnson" className="w-10 h-10 rounded-full object-cover" />
                    <button data-testid="nm-talk-link" type="button" onClick={onBook} className="font-body text-sm text-cvc-red hover:text-cvc-red-deep underline underline-offset-4">
                      Talk It Through with Jonathan →
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-7 rounded-xl bg-cvc-parchment p-5">
        <p className="font-heading text-lg text-cvc-navy">Want another perspective?</p>
        <p className="font-body text-cvc-ink-soft mt-2 text-sm leading-relaxed">{consultCopy}</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button data-testid="nm-book-btn" onClick={onBook} className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-4 py-4 h-auto font-body whitespace-normal leading-tight">
            Talk It Through with Jonathan
          </Button>
          <Button data-testid="nm-email-btn" variant="outline" onClick={onEmail} className="border-cvc-navy text-cvc-navy hover:bg-cvc-navy hover:text-cvc-cream rounded-full px-4 py-4 h-auto font-body bg-transparent whitespace-normal leading-tight">
            Email My Results
          </Button>
          <Button data-testid="nm-assessment-btn" variant="outline" onClick={onAssess} className="border-cvc-navy text-cvc-navy hover:bg-cvc-navy hover:text-cvc-cream rounded-full px-4 py-4 h-auto font-body bg-transparent whitespace-normal leading-tight">
            {content.assessment_handoff.button_label}
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <button
          data-testid="nm-why-toggle"
          type="button"
          onClick={() => setWhyOpen((v) => !v)}
          className="font-body text-sm text-cvc-ink-soft underline underline-offset-4 hover:text-cvc-ink"
        >
          Why am I seeing this?
        </button>
        {whyOpen && (
          <div data-testid="nm-why-body" className="mt-3 rounded-lg bg-white border border-cvc-border p-4 font-body text-sm text-cvc-ink-soft leading-relaxed">
            {whyFragments.map((f, i) => (<p key={f} className={i > 0 ? "mt-2" : ""}>{f}</p>))}
          </div>
        )}
      </div>
      <Disclaimer text={content.disclaimer} testId="nm-results-disclaimer" />
    </div>
  );
}

function EmailScreen({ content, ranked, submissionId, teamSize, onDone, onBack }) {
  const [email, setEmail] = useState("");
  const [opt, setOpt] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!email.includes("@")) return toast.error("Please enter a valid email address.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/next-move/email-results`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          submission_id: submissionId,
          marketing_opt_in: opt,
          team_size: teamSize,
          ranked_concerns: ranked.map((rc) => ({ question_id: rc.questionId, severity: rc.severity, other_text: rc.otherText })),
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Sent — check your inbox.");
      onDone();
    } catch (e) {
      toast.error("Couldn't send right now — please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="p-7 md:p-10">
      <BackRow onBack={onBack} progress="Email a copy" />
      <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy leading-snug">Want a copy of your results?</h2>
      <p className="font-body text-cvc-ink-soft mt-3 text-sm leading-relaxed">
        Enter your email and we'll send you a copy, so you can come back to these ideas whenever you like.
      </p>
      <Input data-testid="nm-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email" className="mt-4 bg-white" />
      <div className="mt-3 flex items-start gap-3">
        <Checkbox id="nm-email-marketing" data-testid="nm-email-marketing" checked={opt}
          onCheckedChange={(v) => setOpt(!!v)} className="mt-1" />
        <Label htmlFor="nm-email-marketing" className="font-body text-xs text-cvc-ink-soft leading-snug">
          Optional: send me occasional business-scaling tips. Unchecked by default.
        </Label>
      </div>
      <div className="mt-5">
        <Button data-testid="nm-email-submit" onClick={send} disabled={busy}
          className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-7 py-5 h-auto font-body disabled:opacity-60">
          {busy ? "Sending..." : "Email my results"}
        </Button>
      </div>
    </div>
  );
}


